/* global google, propel, gtag */

import { __ } from '@wordpress/i18n';
import * as DOMPurify from 'dompurify';

async function initLocationFinder() {
	const maps = [];
	const parser = new DOMParser();
	let mapCentering = false;

	// eslint-disable-next-line no-unused-vars
	const { Map, InfoWindow } = await google.maps.importLibrary('maps');

	// eslint-disable-next-line no-unused-vars
	const { AdvancedMarkerElement, PinElement } =
		await google.maps.importLibrary('marker');

	const addMarker = (result, map, open) => {
		if (
			!result.hasAttribute('data-location') ||
			!result.hasAttribute('data-location-slug') ||
			!result.hasAttribute('data-title')
		) {
			return;
		}

		const locationId = result.getAttribute('data-location');
		let marker;

		if (map.allMarkers[locationId]) {
			marker = map.allMarkers[locationId];
			marker.map = map;
			map.markers.push(marker);
		} else {
			const latlng = new google.maps.LatLng(
				result.getAttribute('data-lat'),
				result.getAttribute('data-lng')
			);

			let markerHtml = result.innerHTML;

			markerHtml = markerHtml.replace('<button', '<span');
			markerHtml = markerHtml.replace('</button', '</span');
			markerHtml = markerHtml.replace('span type="button"', 'span');
			markerHtml = markerHtml.replace(/aria-label="View[^"]*"/gm, '');
			markerHtml = markerHtml.replace(
				'<h2 class="block-location-finder-results__location-title',
				'<h2 id="location-marker-title" class="block-location-finder-results__location-title'
			);

			const markerContentElement = document.createElement('div');
			markerContentElement.setAttribute(
				'data-content',
				`<div class="block-location-finder-marker" aria-labelledby="location-marker-title">${markerHtml}</div>`
			);
			markerContentElement.appendChild(
				parser.parseFromString(
					'<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 18C10 10.26 16.26 4 24 4C31.74 4 38 10.26 38 18C38 28.5 24 44 24 44C24 44 10 28.5 10 18ZM19 18C19 20.76 21.24 23 24 23C26.76 23 29 20.76 29 18C29 15.24 26.76 13 24 13C21.24 13 19 15.24 19 18Z" fill="#253746"/><circle cx="24" cy="18" r="5" fill="white"/></svg>',
					'image/svg+xml'
				).documentElement
			);

			const locationTitle = result.getAttribute('data-title');

			const markerSettings = {
				position: latlng,
				map,
				title: `${__('View details for', 'propel')} ${locationTitle}`,
				content: markerContentElement,
			};

			marker = new AdvancedMarkerElement(markerSettings);

			map.markers.push(marker);

			map.allMarkers[locationId] = marker;

			marker.addListener('click', () => {
				map.infoWindow.close();
				map.infoWindow.setContent(
					marker.content.getAttribute('data-content')
				);
				map.infoWindow.open({
					anchor: marker,
					map: marker.map,
					shouldFocus: false,
				});
			});
		}

		if (open && !map?.stateInput?.value) {
			map.infoWindow.close();
			map.infoWindow.setContent(
				marker.content.getAttribute('data-content')
			);
			map.infoWindow.open({
				anchor: marker,
				map: marker.map,
				shouldFocus: false,
			});
		}

		return marker;
	};

	const centerMap = (map) => {
		mapCentering = true;

		const bounds = new google.maps.LatLngBounds();

		for (let i = 0; i < map.markers.length; i++) {
			bounds.extend(map.markers[i].position);
		}

		if (1 === map.markers.length) {
			map.setCenter(bounds.getCenter());
			map.setZoom(16);
		} else {
			map.fitBounds(bounds, 60);
		}

		mapCentering = false;
	};

	const setLocationCookie = (locationId, locationTitle, locationSlug) => {
		const expirationDate = new Date();
		expirationDate.setDate(expirationDate.getDate() + 30);
		document.cookie = `current_101_mobility_location=${locationId}; expires=${expirationDate.toUTCString()}; path=/`;
		document.cookie = `_101_location=${locationSlug}; expires=${expirationDate.toUTCString()}; path=/`;
		document.cookie = `wpe-us=${locationId}; expires=${expirationDate.toUTCString()}; path=/`;

		// console.log('User Location Set');

		if ('function' === typeof gtag) {
			gtag('event', 'User Location Set', {
				event_category: 'Location Finder',
				event_label: locationTitle,
			});
		}
	};

	const clickOnMap = (e) => {
		if (
			e.target.hasAttribute('data-location') &&
			e.target.hasAttribute('data-title') &&
			e.target.hasAttribute('data-location-slug')
		) {
			setLocationCookie(
				e.target.getAttribute('data-location'),
				e.target.getAttribute('data-title'),
				e.target.getAttribute('data-location-slug')
			);
		}
	};

	const clickResult = (e, marker, map) => {
		if (
			e.target.href ||
			(e.target.tagName && 'SUMMARY' === e.target.tagName)
		) {
			return;
		}

		e.preventDefault();

		map.infoWindow.close();
		map.infoWindow.setContent(marker.content.getAttribute('data-content'));
		map.infoWindow.open({
			anchor: marker,
			map: marker.map,
			shouldFocus: false,
		});

		const activeResults = map.listElement.querySelectorAll(
			'.block-location-finder-results__result--active'
		);

		if (activeResults.length > 0) {
			activeResults.forEach((activeResult) => {
				activeResult.classList.remove(
					'block-location-finder-results__result--active'
				);
			});
		}

		e.currentTarget
			?.closest('.block-location-finder-results__result')
			?.classList.add('block-location-finder-results__result--active');
	};

	const fetchResults = (
		map,
		coordinates = null,
		center = true,
		gtagEvents = true
	) => {
		if (!propel || !propel.siteUrl) {
			return;
		}

		map.blockElement.classList.add(
			'block-location-finder-results--loading'
		);

		const url = new URL(
			`${propel.siteUrl}/wp-json/101-mobility/v1/locations`
		);
		const queryParams = new URLSearchParams();

		if (map.inputElements.length > 0) {
			map.inputElements.forEach((input) => {
				if (input.name && input.value) {
					queryParams.append(input.name, input.value);
				}
			});
		}

		if (coordinates) {
			coordinates.forEach((coordinate) => {
				if (coordinate.name && coordinate.value) {
					queryParams.append(coordinate.name, coordinate.value);
				}
			});
		}

		url.search = queryParams.toString();

		// eslint-disable-next-line no-console
		// console.log(url.href);

		fetch(url)
			.then((response) => {
				if (!response.ok) {
					throw new Error('Network response was not ok');
				}

				return response.json();
			})
			.then((results) => {
				displayResults(results, map, center, gtagEvents);

				setTimeout(function () {
					map.blockElement.classList.remove(
						'block-location-finder-results--loading'
					);
				}, 400);
			})
			.catch((error) => {
				// eslint-disable-next-line no-console
				console.log('error', error);

				setTimeout(function () {
					map.blockElement.classList.remove(
						'block-location-finder-results--loading'
					);
				}, 400);
			});
	};

	const formSubmitted = (e) => {
		if (!e?.detail?.originalEvent && !e?.detail?.autocomplete?.zipInput) {
			return;
		}

		let block;

		if (e?.detail?.originalEvent) {
			e.detail.originalEvent.preventDefault();

			block = e.detail.originalEvent.currentTarget.closest(
				'.block-location-finder-results'
			);
		} else {
			block = e.detail.autocomplete.zipInput.closest(
				'.block-location-finder-results'
			);
		}

		if (!block) {
			return;
		}

		const mapElement = block.querySelector(
			'.block-location-finder-results__map'
		);

		if (!mapElement) {
			return;
		}

		if (!mapElement.hasAttribute('data-map-id')) {
			return;
		}

		const map = maps[mapElement.getAttribute('data-map-id')];

		if (!map) {
			return;
		}

		if (map.markers.length > 0) {
			map.markers.forEach((marker) => {
				if (marker.map) {
					marker.map = null;
				}
			});
		}

		map.markers = [];

		fetchResults(map);
	};

	const displayResults = (results, map, center, gtagEvents = true) => {
		if (!map.listElement || !map.messageElement) {
			return;
		}

		if (
			!map?.stateInput?.value &&
			map?.lInput?.value &&
			'state' === results.search_type
		) {
			map.stateInput.value = map.lInput.value;
		}

		map.listElement.innerHTML = '';

		if (!results.message) {
			results.message = '';
		}

		map.messageElement.innerText = results.message;

		if (results.extended_message) {
			map.listElement.innerHTML += DOMPurify.sanitize(
				results.extended_message
			);

			const resetButton = map.listElement.querySelector(
				'.block-location-finder-results__reset-button'
			);

			if (resetButton) {
				resetButton.addEventListener('click', (e) => {
					e.preventDefault();

					map.lInput.value = '';
					map.messageElement.innerText = '';
					map.listElement.innerText = '';
				});
			}
		}

		if (results.lat && results.lng) {
			if (map.latInput && !map.latInput.value) {
				map.latInput.value = results.lat;
			}

			if (map.lngInput && !map.lngInput.value) {
				map.lngInput.value = results.lng;
			}
		}

		if (results.country && !map.countryInput.value) {
			map.countryInput.value = results.country;
		}

		if (results.locations.length !== 1) {
			setCurrentLocation(map);
		}

		if (results.locations.length > 0) {
			let counter = 0;

			results.locations.forEach((location) => {
				if (!location.title) {
					location.title = '';
				}

				counter++;

				if (location?.coordinates?.lat && location?.coordinates?.lng) {
					const result = document.createElement('div');
					const resultButton = document.createElement('button');
					resultButton.type = 'button';

					result.classList.add(
						'block-location-finder-results__result'
					);

					resultButton.classList.add(
						'block-location-finder-results__result-button'
					);

					if (counter === 1) {
						result.classList.add(
							'block-location-finder-results__result--active'
						);
					}

					result.dataset.lat = location.coordinates.lat;
					result.dataset.lng = location.coordinates.lng;
					result.dataset.title = location.title;
					resultButton.setAttribute(
						'aria-label',
						`${__('View', 'propel')} ${location.title} ${__('on the map', 'propel')}`
					);

					if (location.title) {
						const locationTitle = document.createElement('h2');
						locationTitle.classList.add(
							'block-location-finder-results__location-title'
						);
						resultButton.textContent = `${__('101 Mobility of', 'propel')} ${location.title}`;
						locationTitle.appendChild(resultButton);
						result.appendChild(locationTitle);
					}

					if (location.distance) {
						const locationDistance = document.createElement('em');
						locationDistance.classList.add(
							'block-location-finder-results__location-distance'
						);
						locationDistance.innerHTML = DOMPurify.sanitize(
							location.distance
						);
						result.appendChild(locationDistance);
					}

					if (location.address) {
						const locationAddress = document.createElement('div');
						locationAddress.classList.add(
							'block-location-finder-results__location-address'
						);
						locationAddress.innerHTML = DOMPurify.sanitize(
							`<span class="icon icon-location"><span class="sr-only">${__('Address', 'propel')}</span></span>${location.address}`
						);
						result.appendChild(locationAddress);
					}

					if (location.phone_number) {
						const locationPhone = document.createElement('div');
						locationPhone.classList.add(
							'block-location-finder-results__location-phone'
						);
						locationPhone.innerHTML = DOMPurify.sanitize(
							`<span class="icon icon-phone"><span class="sr-only">${__('Phone Number', 'propel')}</span></span>${location.phone_number}`
						);
						result.appendChild(locationPhone);
					}

					if (location.hours) {
						const locationHours = document.createElement('div');
						locationHours.classList.add(
							'block-location-finder-results__location-hours'
						);
						locationHours.innerHTML = DOMPurify.sanitize(
							`<span class="icon icon-time"><span class="sr-only">${__('Hours', 'propel')}</span></span>${location.hours}`
						);
						result.appendChild(locationHours);
					}

					if (
						location.permalink &&
						location.location_id &&
						location.slug
					) {
						const locationLink = document.createElement('a');
						locationLink.classList.add(
							'block-location-finder-results__location-link'
						);
						locationLink.setAttribute(
							'data-location',
							location.location_id
						);
						locationLink.setAttribute(
							'data-location-slug',
							location.slug
						);
						locationLink.setAttribute('data-title', location.title);
						result.setAttribute(
							'data-location',
							location.location_id
						);
						result.setAttribute(
							'data-location-slug',
							location.slug
						);

						if (
							window.location.hostname &&
							'localhost' === window.location.hostname
						) {
							const regex = new RegExp(`(?<=//)[^/]*`, 'gm');

							location.permalink = location.permalink.replace(
								regex,
								`${window.location.hostname}:${window.location.port}`
							);
						}

						locationLink.href = location.permalink;
						locationLink.textContent = __(
							'Select Location',
							'propel'
						);
						result.appendChild(locationLink);
					}

					map.listElement.appendChild(result);

					const marker = addMarker(
						result,
						map,
						center && 1 === counter ? true : false
					);

					resultButton.addEventListener('click', (e) => {
						clickResult(e, marker, map);
					});
				}
			});

			if (center) {
				centerMap(map);
			}

			if ('function' === typeof gtag && true === gtagEvents) {
				if (map?.zipInput?.value) {
					// console.log('Nearby Locations Displayed');

					gtag('event', 'Nearby Locations Displayed', {
						event_category: 'Location Finder',
						event_label: map.zipInput.value,
					});
				} else if (map?.cityInput?.value) {
					// console.log('City Locations Displayed');

					gtag('event', 'City Locations Displayed', {
						event_category: 'Location Finder',
						event_label: map.cityInput.value,
					});
				} else if (map?.stateInput?.value) {
					// console.log('State Locations Displayed');

					gtag('event', 'State Locations Displayed', {
						event_category: 'Location Finder',
						event_label: map.stateInput.value,
					});
				} else if (map?.lInput?.value) {
					// console.log('Nearby Locations Displayed');

					gtag('event', 'Nearby Locations Displayed', {
						event_category: 'Location Finder',
						event_label: map.lInput.value,
					});
				}
			}
		} else if ('function' === typeof gtag && true === gtagEvents) {
			// console.log('No Locations Found');

			gtag('event', 'No Locations Found', {
				event_category: 'Location Finder',
				event_label: map?.lInput?.value ?? 'unknown',
			});
		}
	};

	const setCurrentLocation = (map) => {
		if (!map?.latInput?.value || !map?.lngInput?.value) {
			return;
		}

		if (map.markers.length > 0) {
			map.markers.forEach((marker) => {
				if (
					marker.map &&
					marker.title &&
					'Current Location' === marker.title
				) {
					marker.map = null;
				}
			});
		}

		const marker = new AdvancedMarkerElement({
			position: new google.maps.LatLng(
				map.latInput.value,
				map.lngInput.value
			),
			title: __('Current Location', 'propel'),
			map,
			content: parser.parseFromString(
				'<svg xmlns="http://www.w3.org/2000/svg" height="32px" viewBox="0 -960 960 960" width="32px" fill="#253746"><path d="M440-42v-80q-125-14-214.5-103.5T122-440H42v-80h80q14-125 103.5-214.5T440-838v-80h80v80q125 14 214.5 103.5T838-520h80v80h-80q-14 125-103.5 214.5T520-122v80h-80Zm40-158q116 0 198-82t82-198q0-116-82-198t-198-82q-116 0-198 82t-82 198q0 116 82 198t198 82Zm0-120q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47Zm0-80q33 0 56.5-23.5T560-480q0-33-23.5-56.5T480-560q-33 0-56.5 23.5T400-480q0 33 23.5 56.5T480-400Zm0-80Z" /></svg>',
				'image/svg+xml'
			).documentElement,
		});

		map.markers.push(marker);
	};

	const mapManuallyChanged = (map) => {
		if (mapCentering) {
			return;
		}

		const bounds = map.getBounds();
		const northEast = bounds.getNorthEast();
		const southWest = bounds.getSouthWest();

		const coordinates = [
			{ name: 'north', value: northEast.lat() },
			{ name: 'south', value: southWest.lat() },
			{ name: 'east', value: northEast.lng() },
			{ name: 'west', value: southWest.lng() },
		];

		fetchResults(map, coordinates, false, false);
	};

	const mapElements = document.querySelectorAll(
		'.block-location-finder-results__map'
	);

	if (mapElements.length > 0) {
		mapElements.forEach((mapElement, index) => {
			mapElement.setAttribute('data-map-id', index);

			const options = {
				center: {
					lat: 39.8283,
					lng: -98.5795,
				},
				zoom: 4,
				mapTypeControl: false,
				fullscreenControl: false,
				streetViewControl: false,
				scaleControl: false,
				zoomControl: true,
				mapId: '898eed5453741db8',
				allMarkers: {},
			};

			const messageElement = mapElement.parentElement.querySelector(
				'.block-location-finder-results__message'
			);
			const listElement = mapElement.parentElement.querySelector(
				'.block-location-finder-results__list'
			);

			const map = new Map(mapElement, options);
			map.markers = [];
			map.infoWindow = new InfoWindow({
				pixelOffset: new google.maps.Size(0, -4),
			});

			if (messageElement) {
				map.messageElement = messageElement;
			}

			if (listElement) {
				map.listElement = listElement;
			}

			map.blockElement = mapElement.parentElement;

			map.inputElements = mapElement.parentElement.querySelectorAll(
				'.block-find-your-location__form input'
			);

			google.maps.event.addListener(map.infoWindow, 'domready', () => {
				const iwOuter = document.querySelector('.gm-style-iw');

				if (iwOuter) {
					iwOuter.addEventListener('click', (e) => {
						if (
							e.target.tagName &&
							'SUMMARY' === e.target.tagName
						) {
							map.infoWindow.open({
								anchor: map.infoWindow.anchor,
								map,
								shouldFocus: false,
							});
						}
					});
				}
			});

			google.maps.event.addListener(map, 'zoom_changed', () => {
				mapManuallyChanged(map);
			});

			google.maps.event.addListener(map, 'dragend', () => {
				mapManuallyChanged(map);
			});

			if (map.inputElements.length > 0) {
				map.inputElements.forEach((input) => {
					if (!input.name) {
						return;
					}

					map[`${input.name}Input`] = input;
				});
			}

			mapElement.parentElement.addEventListener('click', clickOnMap);

			maps.push(map);

			fetchResults(map);
			setCurrentLocation(map);

			window.addEventListener(
				'find-your-location-form-submitted',
				formSubmitted
			);

			map.blockElement.classList.add(
				'block-location-finder-results--initialized'
			);
		});
	}
}

window.addEventListener('google-maps-loaded', initLocationFinder);
